'use strict';

class CloudTestimonialsV2 {
  /**
   * @param {HTMLElement} sectionEl - The .bw-ct section element
   */
  constructor(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId;
    
    this.config = this.parseConfig();
    
    // Slide and state tracking
    this.currentIndex = 0;
    this.totalSlides = 0;
    this.state = 'IDLE'; // IDLE, REVEALING, READING, TRANSITIONING
    this.queuedIndex = null;
    this.revealToken = 0; // Monotonic token to invalidate obsolete timer callbacks
    this.activeTransitionCleanup = null;
    
    // Viewport and pause tracking
    this.isInViewport = false;
    this.hasEnteredViewport = false;
    this.isHeld = false; // Customer tap-to-hold review
    this.isHoverPaused = false;
    this.isFocusPaused = false;
    this.isOffscreenPaused = false;
    this.isDocumentHidden = false;
    
    // Timers
    this.phaseTimers = [];
    this.starTimers = [];
    this.autoplayTimer = null;
    this.recoveryTimer = null;
    
    // Touch tracking
    this.touch = { startX: 0, startY: 0, isTracking: false };
    
    // DOM references
    this.cacheDom();
    
    this.totalSlides = this.cards.length;
    if (this.totalSlides === 0) return;

    // Signal progressive enhancement: JS is active
    this.section.classList.add('is-js-loaded');

    this.bindHandlers();
    this.initCards();
    this.initControls();
    this.initTouchHandling();
    this.initKeyboardNav();
    this.initIntersection();
    this.initVisibility();
    this.initReducedMotion();
    this.stabilizeHeight();
  }

  parseConfig() {
    const deck = this.section.querySelector('.bw-ct__deck');
    if (!deck) return {};
    return {
      autoplay: deck.dataset.autoplay === 'true',
      readingTime: parseInt(deck.dataset.readingTime, 10) || 6000,
      headlineSpeed: parseInt(deck.dataset.headlineSpeed, 10) || 350,
      bodySpeed: parseInt(deck.dataset.bodySpeed, 10) || 450,
      starStagger: parseInt(deck.dataset.starStagger, 10) || 120,
      transitionSpeed: parseInt(deck.dataset.transitionSpeed, 10) || 550,
      animateStars: deck.dataset.animateStars !== 'false',
      starStyle: deck.dataset.starAnimationStyle || 'pop'
    };
  }

  cacheDom() {
    this.clouds = this.section.querySelector('.bw-ct__clouds');
    this.cloudGroups = this.section.querySelectorAll('.bw-ct__cloud-group');
    this.deck = this.section.querySelector('.bw-ct__deck');
    this.deckWrap = this.section.querySelector('.bw-ct__deck-wrap') || this.deck;
    this.cards = Array.prototype.slice.call(this.section.querySelectorAll('.bw-ct__card'));
    this.prevBtn = this.section.querySelector('.bw-ct__prev');
    this.nextBtn = this.section.querySelector('.bw-ct__next');
    this.dots = Array.prototype.slice.call(this.section.querySelectorAll('.bw-ct__dot'));
    this.liveRegion = this.section.querySelector('.bw-ct__live-region');
  }

  bindHandlers() {
    this.handlers = {
      prevClick: () => this.prev(),
      nextClick: () => this.next(),
      dotClick: (e) => {
        const idx = parseInt(e.currentTarget.dataset.index, 10);
        if (!isNaN(idx)) this.goToSlide(idx);
      },
      cardClick: (e) => this.onCardClick(e),
      cardKeydown: (e) => this.onCardKeydown(e),
      deckTouchStart: (e) => this.onTouchStart(e),
      deckTouchMove: (e) => this.onTouchMove(e),
      deckTouchEnd: (e) => this.onTouchEnd(e),
      deckHoverStart: () => { 
        this.isHoverPaused = true; 
        this.clearAutoplayTimer(); 
      },
      deckHoverEnd: () => { 
        this.isHoverPaused = false; 
        if (this.state === 'READING' && !this.isHeld && !this.shouldPauseAutoplay()) {
          this.startAutoplayTimer();
        }
      },
      deckFocusIn: () => { 
        this.isFocusPaused = true; 
        this.clearAutoplayTimer(); 
      },
      deckFocusOut: () => { 
        this.isFocusPaused = false; 
        if (this.state === 'READING' && !this.isHeld && !this.shouldPauseAutoplay()) {
          this.startAutoplayTimer();
        }
      },
      keydown: (e) => this.onKeydown(e),
      visibility: () => this.onVisibilityChange(),
      mqlChange: (e) => this.handleReducedMotion(e),
      fontsReady: () => this.stabilizeHeight(),
      resize: () => this.stabilizeHeight()
    };
  }

  initCards() {
    this.cards.forEach((card, index) => {
      card.dataset.index = index;
      if (index === 0) {
        card.dataset.position = 'active';
        card.dataset.phase = 'hidden';
        card.removeAttribute('inert');
        card.setAttribute('aria-hidden', 'false');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-pressed', 'false');
        card.setAttribute('title', 'Tik om review vast te houden');
        this.resetCardContent(card);
      } else {
        card.dataset.phase = 'hidden';
        card.setAttribute('inert', '');
        card.setAttribute('aria-hidden', 'true');
        card.removeAttribute('tabindex');
        card.removeAttribute('aria-pressed');
        card.removeAttribute('title');
        this.resetCardContent(card);
      }
    });
    this.state = 'IDLE';
    this.positionBackingCards();
  }

  initControls() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlers.prevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.handlers.nextClick);

    this.dots.forEach(dot => {
      dot.addEventListener('click', this.handlers.dotClick);
    });

    this.cards.forEach(card => {
      card.addEventListener('click', this.handlers.cardClick);
      card.addEventListener('keydown', this.handlers.cardKeydown);
    });

    if (this.deck) {
      this.deck.addEventListener('mouseenter', this.handlers.deckHoverStart);
      this.deck.addEventListener('mouseleave', this.handlers.deckHoverEnd);
      this.deck.addEventListener('focusin', this.handlers.deckFocusIn);
      this.deck.addEventListener('focusout', this.handlers.deckFocusOut);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.handlers.fontsReady);
    }
    window.addEventListener('resize', this.handlers.resize);
  }

  onCardClick(e) {
    if (e.target.closest('a, button')) return;
    const card = e.currentTarget;
    if (card.dataset.position !== 'active') return;
    this.toggleHold(card);
  }

  onCardKeydown(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      if (e.target.closest('a, button')) return;
      e.preventDefault();
      const card = e.currentTarget;
      if (card.dataset.position !== 'active') return;
      this.toggleHold(card);
    }
  }

  toggleHold(card) {
    if (!card) card = this.cards[this.currentIndex];
    if (!card || card.dataset.position !== 'active') return;

    this.isHeld = !this.isHeld;

    if (this.isHeld) {
      card.classList.add('is-held');
      card.setAttribute('aria-pressed', 'true');
      card.setAttribute('title', 'Gepauzeerd (tik om te hervatten)');
      this.settleCard(card);
      this.clearAutoplayTimer();
    } else {
      card.classList.remove('is-held');
      card.setAttribute('aria-pressed', 'false');
      card.setAttribute('title', 'Tik om review vast te houden');
      if (this.state === 'READING' && !this.shouldPauseAutoplay()) {
        this.startAutoplayTimer();
      }
    }
  }

  clearHold() {
    this.isHeld = false;
    const activeCard = this.cards[this.currentIndex];
    if (activeCard) {
      activeCard.classList.remove('is-held');
      activeCard.setAttribute('aria-pressed', 'false');
      activeCard.setAttribute('title', 'Tik om review vast te houden');
    }
  }

  settleCard(card) {
    if (!card) card = this.cards[this.currentIndex];
    if (!card) return;
    
    // Invalidate any active reveal callbacks
    this.revealToken++;
    this.clearPhaseTimers();
    
    card.dataset.phase = 'reading';
    this.revealAllStars(card);
    const glint = card.querySelector('.bw-ct__stars-glint');
    if (glint) glint.classList.remove('animate-glint');
    
    this.state = 'READING';
  }

  initTouchHandling() {
    if (!this.deckWrap) return;
    this.deckWrap.addEventListener('pointerdown', this.handlers.deckTouchStart);
    this.deckWrap.addEventListener('pointermove', this.handlers.deckTouchMove, { passive: false });
    this.deckWrap.addEventListener('pointerup', this.handlers.deckTouchEnd);
    this.deckWrap.addEventListener('pointercancel', this.handlers.deckTouchEnd);
  }

  onTouchStart(e) {
    if (!e.isPrimary) return;
    this.touch.startX = e.clientX;
    this.touch.startY = e.clientY;
    this.touch.isTracking = true;
  }

  onTouchMove(e) {
    if (!this.touch.isTracking || !e.isPrimary) return;
    const diffX = e.clientX - this.touch.startX;
    const diffY = e.clientY - this.touch.startY;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      if (e.cancelable) e.preventDefault();
    }
  }

  onTouchEnd(e) {
    if (!this.touch.isTracking) return;
    this.touch.isTracking = false;
    const diffX = e.clientX - this.touch.startX;
    const diffY = e.clientY - this.touch.startY;
    const threshold = 40;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        this.prev();
      } else {
        this.next();
      }
    }
  }

  initKeyboardNav() {
    this.section.addEventListener('keydown', this.handlers.keydown);
  }

  onKeydown(e) {
    if (!this.section.contains(document.activeElement)) return;
    if (e.key === 'ArrowLeft') {
      this.prev();
    } else if (e.key === 'ArrowRight') {
      this.next();
    }
  }

  initIntersection() {
    if (!('IntersectionObserver' in window)) {
      this.hasEnteredViewport = true;
      this.isInViewport = true;
      this.startRevealSequence();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const isVisible = entry.isIntersecting;
        // Idempotency check: ignore duplicate status notifications
        if (isVisible === this.isInViewport && this.hasEnteredViewport) {
          return;
        }
        this.isInViewport = isVisible;

        if (isVisible) {
          this.handleViewportEnter();
        } else {
          this.handleViewportExit();
        }
      });
    }, { threshold: [0, 0.15] });

    this.observer.observe(this.section);
  }

  handleViewportEnter() {
    this.isOffscreenPaused = false;
    this.resumeCloudAnimations();

    // 1. First entry: trigger normal entrance reveal
    if (!this.hasEnteredViewport) {
      this.hasEnteredViewport = true;
      this.startRevealSequence();
      return;
    }

    // 2. Re-entry: ensure current card is fully readable; do not restart reveal
    const activeCard = this.cards[this.currentIndex];
    if (activeCard && activeCard.dataset.phase !== 'reading') {
      this.settleCard(activeCard);
    }
    this.state = 'READING';

    // 3. Resume autoplay with fresh interval only if card is NOT held
    if (!this.isHeld && !this.shouldPauseAutoplay()) {
      this.startAutoplayTimer();
    }
  }

  handleViewportExit() {
    this.isOffscreenPaused = true;
    this.pauseCloudAnimations();
    this.clearAutoplayTimer();

    // Safely finish any underway deck transition to its destination
    if (this.state === 'TRANSITIONING') {
      if (typeof this.activeTransitionCleanup === 'function') {
        this.activeTransitionCleanup();
        this.activeTransitionCleanup = null;
      } else {
        this.positionCards();
        this.positionBackingCards();
        this.section.classList.remove('is-transitioning');
      }
    }

    // Safely settle the active card into fully readable state
    const activeCard = this.cards[this.currentIndex];
    if (activeCard) {
      this.settleCard(activeCard);
    }
    this.state = 'READING';
    this.hasEnteredViewport = true;
  }

  initVisibility() {
    document.addEventListener('visibilitychange', this.handlers.visibility);
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.isDocumentHidden = true;
      this.pauseCloudAnimations();
      this.clearAutoplayTimer();
      
      if (this.state === 'TRANSITIONING' && typeof this.activeTransitionCleanup === 'function') {
        this.activeTransitionCleanup();
        this.activeTransitionCleanup = null;
      }
      const activeCard = this.cards[this.currentIndex];
      if (activeCard) this.settleCard(activeCard);
      this.state = 'READING';
    } else {
      this.isDocumentHidden = false;
      if (!this.isOffscreenPaused) {
        this.resumeCloudAnimations();
        if (this.state === 'READING' && !this.isHeld && !this.shouldPauseAutoplay()) {
          this.startAutoplayTimer();
        }
      }
    }
  }

  initReducedMotion() {
    this.mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mql.addEventListener('change', this.handlers.mqlChange);
    this.handlers.mqlChange(this.mql);
  }

  handleReducedMotion(e) {
    if (e.matches) {
      this.section.classList.add('is-reduced-motion');
      this.pauseCloudAnimations();
      const currentCard = this.cards[this.currentIndex];
      if (currentCard) this.settleCard(currentCard);
    } else {
      this.section.classList.remove('is-reduced-motion');
      if (!this.isOffscreenPaused && !this.isDocumentHidden) {
        this.resumeCloudAnimations();
      }
    }
  }

  next() {
    const target = (this.currentIndex + 1) % this.totalSlides;
    this.goToSlide(target, false, 'next');
  }

  prev() {
    const target = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(target, false, 'prev');
  }

  goToSlide(index, immediate = false, direction = null) {
    if (index === this.currentIndex && this.state !== 'IDLE' && this.state !== 'READING') return;
    
    if (this.state === 'TRANSITIONING') {
      this.queuedIndex = index;
      return;
    }

    // Clear hold state on slide change
    this.clearHold();
    this.clearAutoplayTimer();
    this.clearPhaseTimers();
    
    if (!direction) {
      direction = index > this.currentIndex ? 'next' : 'prev';
    }
    
    const oldIndex = this.currentIndex;
    this.currentIndex = index;
    
    this.updateDots();
    this.announceSlide();
    
    if (immediate || this.section.classList.contains('is-reduced-motion')) {
      this.positionCards();
      this.startRevealSequence();
      return;
    }
    
    this.state = 'TRANSITIONING';
    this.section.classList.add('is-transitioning');
    
    const exitDirection = direction === 'next' ? 'exit-left' : 'exit-right';
    const oldCard = this.cards[oldIndex];
    const newCard = this.cards[index];
    
    if (oldCard) {
      oldCard.dataset.position = exitDirection;
      oldCard.dataset.phase = 'hidden';
      oldCard.removeAttribute('tabindex');
      oldCard.removeAttribute('aria-pressed');
      oldCard.removeAttribute('title');
      this.resetCardContent(oldCard);
    }
    
    if (newCard) {
      newCard.dataset.position = direction === 'next' ? 'enter' : 'enter-prev';
      newCard.removeAttribute('inert');
      newCard.setAttribute('aria-hidden', 'false');
      newCard.setAttribute('tabindex', '0');
      newCard.setAttribute('aria-pressed', 'false');
      newCard.setAttribute('title', 'Tik om review vast te houden');
    }
    
    let isDone = false;
    const transitionComplete = () => {
      if (isDone) return;
      isDone = true;
      this.activeTransitionCleanup = null;
      
      if (oldCard) {
        oldCard.removeEventListener('animationend', transitionComplete);
        oldCard.dataset.position = 'hidden';
        oldCard.setAttribute('aria-hidden', 'true');
        oldCard.setAttribute('inert', '');
      }
      if (newCard) {
        newCard.removeEventListener('animationend', transitionComplete);
        newCard.dataset.position = 'active';
      }
      
      if (transitionTimer) clearTimeout(transitionTimer);
      
      this.positionBackingCards();
      this.state = 'IDLE';
      this.section.classList.remove('is-transitioning');
      
      if (this.queuedIndex !== null) {
        const qi = this.queuedIndex;
        this.queuedIndex = null;
        this.goToSlide(qi);
      } else if (this.isOffscreenPaused || this.isDocumentHidden) {
        this.settleCard(newCard);
        this.state = 'READING';
      } else {
        this.startRevealSequence();
      }
    };

    this.activeTransitionCleanup = () => {
      if (isDone) return;
      isDone = true;
      if (oldCard) {
        oldCard.removeEventListener('animationend', transitionComplete);
        oldCard.dataset.position = 'hidden';
        oldCard.setAttribute('aria-hidden', 'true');
        oldCard.setAttribute('inert', '');
      }
      if (newCard) {
        newCard.removeEventListener('animationend', transitionComplete);
        newCard.dataset.position = 'active';
      }
      if (transitionTimer) clearTimeout(transitionTimer);
      this.positionBackingCards();
      this.section.classList.remove('is-transitioning');
      this.settleCard(newCard);
      this.state = 'READING';
    };
    
    if (oldCard) oldCard.addEventListener('animationend', transitionComplete);
    if (newCard) newCard.addEventListener('animationend', transitionComplete);
    
    const transitionTimer = setTimeout(transitionComplete, this.config.transitionSpeed + 50);
    this.phaseTimers.push(transitionTimer);
  }

  positionCards() {
    this.cards.forEach((card, i) => {
      if (i === this.currentIndex) {
        card.dataset.position = 'active';
        card.removeAttribute('inert');
        card.setAttribute('aria-hidden', 'false');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-pressed', 'false');
        card.setAttribute('title', 'Tik om review vast te houden');
      } else {
        card.dataset.position = 'hidden';
        card.setAttribute('inert', '');
        card.setAttribute('aria-hidden', 'true');
        card.removeAttribute('tabindex');
        card.removeAttribute('aria-pressed');
        card.removeAttribute('title');
      }
    });
    this.positionBackingCards();
  }

  positionBackingCards() {
    const total = this.cards.length;
    if (total <= 1) return;
    
    const b1Index = (this.currentIndex + 1) % total;
    const b2Index = total > 2 ? (this.currentIndex + 2) % total : -1;
    
    this.cards.forEach((card, i) => {
      if (i === this.currentIndex) return;
      if (i === b1Index) {
        card.dataset.position = 'back-1';
        card.setAttribute('inert', '');
        card.setAttribute('aria-hidden', 'true');
      } else if (i === b2Index) {
        card.dataset.position = 'back-2';
        card.setAttribute('inert', '');
        card.setAttribute('aria-hidden', 'true');
      } else {
        card.dataset.position = 'hidden';
        card.setAttribute('inert', '');
        card.setAttribute('aria-hidden', 'true');
      }
    });
  }

  startRevealSequence() {
    const card = this.cards[this.currentIndex];
    if (!card) return;
    
    this.state = 'REVEALING';
    const token = ++this.revealToken;
    
    if (this.section.classList.contains('is-reduced-motion') || this.isHeld || this.isOffscreenPaused) {
      this.settleCard(card);
      if (!this.shouldPauseAutoplay() && !this.isOffscreenPaused) this.startAutoplayTimer();
      return;
    }

    this.clearPhaseTimers();
    this.resetCardContent(card);

    // Watchdog
    const recoveryTimer = setTimeout(() => {
      if (this.revealToken !== token) return;
      const curCard = this.cards[this.currentIndex];
      if (curCard && curCard.dataset.phase !== 'reading') {
        this.settleCard(curCard);
        if (!this.shouldPauseAutoplay() && !this.isOffscreenPaused) this.startAutoplayTimer();
      }
    }, 2500);
    this.phaseTimers.push(recoveryTimer);
    
    const t1 = setTimeout(() => {
      if (this.revealToken !== token) return;
      card.dataset.phase = 'headline';
      
      const t2 = setTimeout(() => {
        if (this.revealToken !== token) return;
        card.dataset.phase = 'body';
        
        const t3 = setTimeout(() => {
          if (this.revealToken !== token) return;
          card.dataset.phase = 'author';
          
          const t4 = setTimeout(() => {
            if (this.revealToken !== token) return;
            card.dataset.phase = 'stars';
            this.staggerStars(card, token);
          }, 250);
          this.phaseTimers.push(t4);
          
        }, this.config.bodySpeed);
        this.phaseTimers.push(t3);
        
      }, this.config.headlineSpeed);
      this.phaseTimers.push(t2);
      
    }, 80);
    this.phaseTimers.push(t1);
  }

  staggerStars(card, token) {
    const stars = card.querySelectorAll('.bw-ct__star');
    const glint = card.querySelector('.bw-ct__stars-glint');
    
    if (!this.config.animateStars || stars.length === 0) {
      this.revealAllStars(card);
      this.finishReveal(token);
      return;
    }
    
    stars.forEach((star, idx) => {
      const t = setTimeout(() => {
        if (this.revealToken !== token) return;
        star.classList.add('is-revealed');
        
        if (idx === stars.length - 1) {
          const gt = setTimeout(() => {
            if (this.revealToken !== token) return;
            if (glint) glint.classList.add('animate-glint');
            this.finishReveal(token);
          }, 200);
          this.starTimers.push(gt);
        }
      }, idx * this.config.starStagger);
      this.starTimers.push(t);
    });
  }

  revealAllStars(card) {
    const stars = card.querySelectorAll('.bw-ct__star');
    stars.forEach(s => s.classList.add('is-revealed'));
  }

  finishReveal(token) {
    if (token && this.revealToken !== token) return;
    const card = this.cards[this.currentIndex];
    if (card) card.dataset.phase = 'reading';
    this.state = 'READING';
    this.clearPhaseTimers();
    if (!this.shouldPauseAutoplay() && !this.isOffscreenPaused) {
      this.startAutoplayTimer();
    }
  }

  resetCardContent(card) {
    card.dataset.phase = 'hidden';
    const stars = card.querySelectorAll('.bw-ct__star');
    stars.forEach(s => s.classList.remove('is-revealed'));
    const glint = card.querySelector('.bw-ct__stars-glint');
    if (glint) glint.classList.remove('animate-glint');
  }

  skipToReading() {
    const card = this.cards[this.currentIndex];
    this.settleCard(card);
    if (!this.shouldPauseAutoplay() && !this.isOffscreenPaused) this.startAutoplayTimer();
  }

  startAutoplayTimer() {
    this.clearAutoplayTimer();
    
    if (!this.config.autoplay || this.shouldPauseAutoplay() || this.isHeld) return;
    
    const card = this.cards[this.currentIndex];
    const bodyEl = card ? card.querySelector('.bw-ct__body') : null;
    const bodyText = bodyEl ? bodyEl.textContent : '';
    const wordCount = bodyText.trim().split(/\s+/).filter(Boolean).length;
    const dynamicTime = Math.max(this.config.readingTime, 6000 + wordCount * 120);
    
    this.autoplayTimer = setTimeout(() => {
      this.next();
    }, dynamicTime);
  }

  clearAutoplayTimer() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  clearPhaseTimers() {
    this.phaseTimers.forEach(t => clearTimeout(t));
    this.phaseTimers = [];
    this.starTimers.forEach(t => clearTimeout(t));
    this.starTimers = [];
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
  }

  clearAllTimers() {
    this.clearPhaseTimers();
    this.clearAutoplayTimer();
  }

  shouldPauseAutoplay() {
    return this.isHeld ||
           this.isHoverPaused || 
           this.isFocusPaused ||
           this.isOffscreenPaused || 
           this.isDocumentHidden;
  }

  updateDots() {
    this.dots.forEach((dot, idx) => {
      const isActive = idx === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  announceSlide() {
    if (!this.liveRegion) return;
    const total = this.totalSlides;
    const current = this.currentIndex + 1;
    this.liveRegion.textContent = 'Review ' + current + ' van ' + total;
  }

  stabilizeHeight() {
    if (!this.deck) return;
    
    let maxHeight = 0;
    this.cards.forEach(card => {
      const h = card.scrollHeight;
      if (h > maxHeight) maxHeight = h;
    });
    
    if (maxHeight > 0) {
      this.deck.style.minHeight = maxHeight + 'px';
    }
  }

  pauseCloudAnimations() {
    this.cloudGroups.forEach(group => {
      group.style.animationPlayState = 'paused';
    });
  }

  resumeCloudAnimations() {
    this.cloudGroups.forEach(group => {
      group.style.animationPlayState = 'running';
    });
  }

  destroy() {
    this.clearAllTimers();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this.handlers.visibility);
    if (this.mql && this.mql.removeEventListener) {
      this.mql.removeEventListener('change', this.handlers.mqlChange);
    }
    
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlers.prevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handlers.nextClick);
    
    this.dots.forEach(dot => {
      dot.removeEventListener('click', this.handlers.dotClick);
    });

    this.cards.forEach(card => {
      card.removeEventListener('click', this.handlers.cardClick);
      card.removeEventListener('keydown', this.handlers.cardKeydown);
    });
    
    if (this.deck) {
      this.deck.removeEventListener('mouseenter', this.handlers.deckHoverStart);
      this.deck.removeEventListener('mouseleave', this.handlers.deckHoverEnd);
      this.deck.removeEventListener('focusin', this.handlers.deckFocusIn);
      this.deck.removeEventListener('focusout', this.handlers.deckFocusOut);
    }

    if (this.deckWrap) {
      this.deckWrap.removeEventListener('pointerdown', this.handlers.deckTouchStart);
      this.deckWrap.removeEventListener('pointermove', this.handlers.deckTouchMove);
      this.deckWrap.removeEventListener('pointerup', this.handlers.deckTouchEnd);
      this.deckWrap.removeEventListener('pointercancel', this.handlers.deckTouchEnd);
    }

    this.section.removeEventListener('keydown', this.handlers.keydown);
    window.removeEventListener('resize', this.handlers.resize);

    this.section.classList.remove('is-js-loaded', 'is-transitioning', 'is-reduced-motion', 'is-motion-paused');
    this.resumeCloudAnimations();
    
    delete this.section.__bwCarousel;
  }
}

// Initialize on DOMContentLoaded
function initCloudTestimonials() {
  document.querySelectorAll('.bw-ct').forEach(section => {
    if (!section.__bwCarousel) {
      section.__bwCarousel = new CloudTestimonialsV2(section);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCloudTestimonials);
} else {
  initCloudTestimonials();
}

// Theme editor lifecycle
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.bw-ct');
  if (!section) return;
  if (section.__bwCarousel) section.__bwCarousel.destroy();
  section.__bwCarousel = new CloudTestimonialsV2(section);
});

document.addEventListener('shopify:section:unload', (e) => {
  const section = e.target.querySelector('.bw-ct');
  if (section && section.__bwCarousel) {
    section.__bwCarousel.destroy();
  }
});

document.addEventListener('shopify:block:select', (e) => {
  const section = e.target.closest('.bw-ct');
  if (!section || !section.__bwCarousel) return;
  const blockId = e.detail.blockId;
  const card = section.querySelector(`.bw-ct__card[data-block-id="${blockId}"]`);
  if (card) {
    const idx = parseInt(card.dataset.index, 10);
    section.__bwCarousel.goToSlide(idx, true);
  }
});
